import { CostumeTextureID, CostumeTextureSet } from "./CostumeTexture";

type CostumeCategory =
  | "髪型"
  | "眉"
  | "目"
  | "口"
  | "腕"
  | "服装"
  | "帽子"
  | "小物"
  | "背景"
  | "メガネ"
  | "耳飾り"
  | "目の色";

interface CostumeCategoryInfo {
  required: boolean;
  multipleChoice: boolean;
}

type CostumeCategoryRegistry = {
  [key in CostumeCategory]: CostumeCategoryInfo;
};

const costumeCategoryRegistry: CostumeCategoryRegistry = {
  髪型: {
    required: true,
    multipleChoice: false,
  },
  眉: {
    required: true,
    multipleChoice: false,
  },
  目: {
    required: true,
    multipleChoice: false,
  },
  口: {
    required: true,
    multipleChoice: false,
  },
  腕: {
    required: true,
    multipleChoice: false,
  },
  服装: {
    required: true,
    multipleChoice: false,
  },
  帽子: {
    required: true,
    multipleChoice: false,
  },
  小物: {
    required: false,
    multipleChoice: true,
  },
  メガネ: {
    required: false,
    multipleChoice: false,
  },
  耳飾り: {
    required: false,
    multipleChoice: false,
  },
  目の色: {
    required: true,
    multipleChoice: false,
  },
  背景: {
    required: true,
    multipleChoice: false,
  },
};

enum CostumeID {
  // Hairs
  HairNormal = 101,
  HairTied = 102,
  HairFurisode = 103,
  HairBraidMaid = 104,
  HairBraidShort = 105,
  HairBrushedUp = 106,
  HairAku = 107,
  // Eyebrows
  EyebrowsNormal = 201,
  EyebrowsSmile = 202,
  EyebrowsAngry = 203,
  EyebrowsSad = 204,
  EyebrowsSmug = 205,
  EyebrowsTroubled = 206,
  // Eyes
  EyesNormal = 301,
  EyesSurprised = 302,
  EyesUpper = 303,
  EyesSlightlyClosed = 304,
  EyesScornful = 305,
  EyesDowncast = 306,
  EyesClosedSilent = 307,
  EyesClosedSmile = 308,
  EyesArrow = 309,
  EyesShitake = 310,
  EyesRightWink1 = 311,
  EyesLeftWink1 = 312,
  EyesRightWink2 = 313,
  EyesLeftWink2 = 314,
  // Eye Colors
  EyeColorNormal = 380,
  EyeColorAku = 390,
  // Mouth
  MouthClosed = 401,
  MouthSlightlyOpened = 402,
  MouthOpened = 403,
  MouthWidelyOpened = 404,
  MouthWhite = 405,
  MouthCat = 406,
  MouthWithTongue = 407,
  MouthAngry = 408,
  MouthStupified = 409,
  MouthGrin = 410,
  MouthBlank = 411,
  MouthStronglyClothed = 412,
  MouthWaved = 413,
  MouthDot = 414,
  MouthVampire = 415,
  // Arms
  ArmsNone = 501,
  ArmsBye = 502,
  ArmsPen = 503,
  ArmsCat = 504,
  ArmsGao = 505,
  ArmsIndexFinger = 506,
  ArmsToMouth = 507,
  ArmsHeart = 508,
  ArmsAkinium = 509,
  // Clothes
  ClothesNormal = 601,
  ClothesSummer = 602,
  ClothesSchool = 603,
  ClothesFurisode = 604,
  ClothesFurisodeWithShawl = 605,
  ClothesMaid = 606,
  ClothesWhiteTurtleneck = 607,
  ClothesBlackShirt = 608,
  ClothesBlackShirtWithSacoche = 609,
  ClothesAku = 610,
  // Caps
  CapNone = 701,
  CapCrown = 702,
  CapWhite = 703,
  CapSchool = 704,
  CapCasquette = 705,
  CapHeaddress = 706,
  CapHalloween = 707,
  // Glasses
  GlassesNone = 801,
  GlassesNormal = 802,
  GlassesCinnamoroll = 803,
  // Earrings
  EarringsNone = 805,
  EarringsVampire = 806,
  EarringsFur = 807,
  // Accessories
  AccessoryAngryMark = 811,
  AccessoryTeardrop = 812,
  AccessoryMicrophone = 813,
  AccessoryHairpinsVampire = 814,
  AccessoryHairpin = 815,
  AccessorySparkle = 816,
  AccessoryHearts = 817,
  AccessoryHead = 818,
  AccessoryWing = 819,
  AccessoryCheek1 = 820,
  AccessoryCheek2 = 821,
  AccessoryCheek3 = 822,
  // Backgrounds
  BackgroundCaffe = 901,
  BackgroundLivingRoom = 902,
  BackgroundJapaneseRoom = 903,
  BackgroundAquarium = 904,
  BackgroundAmusementPark1 = 905,
  BackgroundAmusementPark2 = 906,
  BackgroundFlowerGarden = 907,
  BackgroundClassRoom = 908,
  BackgroundNightView = 909,
  BackgroundNightSky = 910,
  BackgroundSpring = 911,
  BackgroundSummer1 = 912,
  BackgroundSummer2 = 913,
  BackgroundSummer3 = 914,
  BackgroundFall1 = 915,
  BackgroundFall2 = 916,
  BackgroundWinter1 = 917,
  BackgroundWinter2 = 918,
  BackgroundSpace = 919,
  BackgroundTransparent = 930,
}

type CostumeSet = Set<CostumeID>;

interface CostumeInfo {
  category: CostumeCategory;
  name: string;
  textures:
    | CostumeTextureSet
    | ((costumes: CostumeSet) => Set<CostumeTextureID>);
}

type CostumeRegistry = { [key in CostumeID]: CostumeInfo };

const costumeRegistry: CostumeRegistry = {
  // Hairs
  [CostumeID.HairNormal]: {
    category: "髪型",
    name: "通常",
    textures: new Set([
      CostumeTextureID.HairBackNormal,
      CostumeTextureID.HairShadowNormal,
      CostumeTextureID.HairFrontNormal,
    ]),
  },
  [CostumeID.HairTied]: {
    category: "髪型",
    name: "ひとつ結び",
    textures: new Set([
      CostumeTextureID.HairBackTied,
      CostumeTextureID.HairShadowTied,
      CostumeTextureID.HairFrontTied,
    ]),
  },
  [CostumeID.HairFurisode]: {
    category: "髪型",
    name: "振り袖結",
    textures: new Set([
      CostumeTextureID.HairBackFurisode,
      CostumeTextureID.HairShadowFurisode,
      CostumeTextureID.HairFrontFurisode,
    ]),
  },
  [CostumeID.HairBraidMaid]: {
    category: "髪型",
    name: "編み込み（メイド）",
    textures: new Set([
      CostumeTextureID.HairBackBraidMaid,
      CostumeTextureID.HairShadowBraidMaid,
      CostumeTextureID.HairFrontBraidMaid,
    ]),
  },
  [CostumeID.HairBraidShort]: {
    category: "髪型",
    name: "編み込み（ショート）",
    textures: new Set([
      CostumeTextureID.HairBackBraidShort,
      CostumeTextureID.HairShadowBraidShort,
      CostumeTextureID.HairFrontBraidShort,
    ]),
  },
  [CostumeID.HairBrushedUp]: {
    category: "髪型",
    name: "でこっぱち",
    textures: new Set([
      CostumeTextureID.HairBackBrushedUp,
      CostumeTextureID.HairShadowBrushedUp,
      CostumeTextureID.HairFrontBrushedUp,
    ]),
  },
  [CostumeID.HairAku]: {
    category: "髪型",
    name: "アクくんカラー",
    textures: new Set([
      CostumeTextureID.HairBackAku,
      CostumeTextureID.HairShadowAku,
      CostumeTextureID.HairFrontAku,
    ]),
  },
  // Eyebrows
  [CostumeID.EyebrowsNormal]: {
    category: "眉",
    name: "通常",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.EyebrowsNormalAku]);
      } else {
        return new Set([CostumeTextureID.EyebrowsNormal]);
      }
    },
  },
  [CostumeID.EyebrowsSmile]: {
    category: "眉",
    name: "喜び",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.EyebrowsSmileAku]);
      } else {
        return new Set([CostumeTextureID.EyebrowsSmile]);
      }
    },
  },
  [CostumeID.EyebrowsAngry]: {
    category: "眉",
    name: "怒る",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.EyebrowsAngryAku]);
      } else {
        return new Set([CostumeTextureID.EyebrowsAngry]);
      }
    },
  },
  [CostumeID.EyebrowsSad]: {
    category: "眉",
    name: "哀しい",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.EyebrowsSadAku]);
      } else {
        return new Set([CostumeTextureID.EyebrowsSad]);
      }
    },
  },
  [CostumeID.EyebrowsSmug]: {
    category: "眉",
    name: "ドヤ",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.EyebrowsSmugAku]);
      } else {
        return new Set([CostumeTextureID.EyebrowsSmug]);
      }
    },
  },
  [CostumeID.EyebrowsTroubled]: {
    category: "眉",
    name: "困る",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.EyebrowsTroubledAku]);
      } else {
        return new Set([CostumeTextureID.EyebrowsTroubled]);
      }
    },
  },
  // Eyes

  [CostumeID.EyesNormal]: {
    category: "目",
    name: "通常",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesNormalAku,
          CostumeTextureID.EyesNormalAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesNormal,
          CostumeTextureID.EyesNormalAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesSurprised]: {
    category: "目",
    name: "ハッ",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesSurprisedAku,
          CostumeTextureID.EyesSurprisedAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesSurprised,
          CostumeTextureID.EyesSurprisedAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesUpper]: {
    category: "目",
    name: "視線上",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesUpperAku,
          CostumeTextureID.EyesUpperAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesUpper,
          CostumeTextureID.EyesUpperAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesSlightlyClosed]: {
    category: "目",
    name: "ちょい閉じ",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesSlightlyClosedAku,
          CostumeTextureID.EyesSlightlyClosedAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesSlightlyClosed,
          CostumeTextureID.EyesSlightlyClosedAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesScornful]: {
    category: "目",
    name: "ジト目",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesScornfulAku,
          CostumeTextureID.EyesScornfulAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesScornful,
          CostumeTextureID.EyesScornfulAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesDowncast]: {
    category: "目",
    name: "伏目",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesDowncastAku,
          CostumeTextureID.EyesDowncastAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesDowncast,
          CostumeTextureID.EyesDowncastAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesClosedSilent]: {
    category: "目",
    name: "目閉じ( ˘　˘ )",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesClosedSilentAku,
          CostumeTextureID.EyesClosedSilentAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesClosedSilent,
          CostumeTextureID.EyesClosedSilentAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesClosedSmile]: {
    category: "目",
    name: "目閉じ(＾ ＾)",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesClosedSmileAku,
          CostumeTextureID.EyesClosedSmileAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesClosedSmile,
          CostumeTextureID.EyesClosedSmileAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesArrow]: {
    category: "目",
    name: "＞＜",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesArrowAku,
          CostumeTextureID.EyesArrowAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesArrow,
          CostumeTextureID.EyesArrowAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesShitake]: {
    category: "目",
    name: "しいたけ",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesShitakeAku,
          CostumeTextureID.EyesShitakeAkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesShitake,
          CostumeTextureID.EyesShitakeAlpha,
        ]);
      }
    },
  },
  [CostumeID.EyesRightWink1]: {
    category: "目",
    name: "右ウィンク1",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesRightWink1Aku,
          CostumeTextureID.EyesRightWink1AkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesRightWink1,
          CostumeTextureID.EyesRightWink1Alpha,
        ]);
      }
    },
  },
  [CostumeID.EyesLeftWink1]: {
    category: "目",
    name: "左ウィンク1",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesLeftWink1Aku,
          CostumeTextureID.EyesLeftWink1AkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesLeftWink1,
          CostumeTextureID.EyesLeftWink1Alpha,
        ]);
      }
    },
  },
  [CostumeID.EyesRightWink2]: {
    category: "目",
    name: "右ウィンク2",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesRightWink2Aku,
          CostumeTextureID.EyesRightWink2AkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesRightWink2,
          CostumeTextureID.EyesRightWink2Alpha,
        ]);
      }
    },
  },
  [CostumeID.EyesLeftWink2]: {
    category: "目",
    name: "左ウィンク2",
    textures: (costumes: CostumeSet) => {
      if (costumes.has(CostumeID.EyeColorAku)) {
        return new Set([
          CostumeTextureID.EyesLeftWink2Aku,
          CostumeTextureID.EyesLeftWink2AkuAlpha,
        ]);
      } else {
        return new Set([
          CostumeTextureID.EyesLeftWink2,
          CostumeTextureID.EyesLeftWink2Alpha,
        ]);
      }
    },
  },
  // Eye Colors
  [CostumeID.EyeColorNormal]: {
    category: "目の色",
    name: "通常",
    textures: new Set([]),
  },
  [CostumeID.EyeColorAku]: {
    category: "目の色",
    name: "アクくんカラー",
    textures: new Set([]),
  },
  // Mouth
  [CostumeID.MouthClosed]: {
    category: "口",
    name: "笑顔1（口閉じ）",
    textures: new Set([CostumeTextureID.MouthClosed]),
  },
  [CostumeID.MouthSlightlyOpened]: {
    category: "口",
    name: "笑顔2（口開け小）",
    textures: new Set([CostumeTextureID.MouthSlightlyOpened]),
  },
  [CostumeID.MouthOpened]: {
    category: "口",
    name: "笑顔3（口開け大）",
    textures: new Set([CostumeTextureID.MouthOpened]),
  },
  [CostumeID.MouthWidelyOpened]: {
    category: "口",
    name: "笑顔4（口開け縦長）",
    textures: new Set([CostumeTextureID.MouthWidelyOpened]),
  },
  [CostumeID.MouthWhite]: {
    category: "口",
    name: "笑顔5（白）",
    textures: new Set([CostumeTextureID.MouthWhite]),
  },
  [CostumeID.MouthCat]: {
    category: "口",
    name: "猫っぽい笑顔",
    textures: new Set([CostumeTextureID.MouthCat]),
  },
  [CostumeID.MouthWithTongue]: {
    category: "口",
    name: "ぺろ",
    textures: new Set([CostumeTextureID.MouthWithTongue]),
  },
  [CostumeID.MouthAngry]: {
    category: "口",
    name: "むすっ",
    textures: new Set([CostumeTextureID.MouthAngry]),
  },
  [CostumeID.MouthStupified]: {
    category: "口",
    name: "きょとん",
    textures: new Set([CostumeTextureID.MouthStupified]),
  },
  [CostumeID.MouthGrin]: {
    category: "口",
    name: "にやり",
    textures: new Set([CostumeTextureID.MouthGrin]),
  },
  [CostumeID.MouthBlank]: {
    category: "口",
    name: "ぽかん",
    textures: new Set([CostumeTextureID.MouthBlank]),
  },
  [CostumeID.MouthStronglyClothed]: {
    category: "口",
    name: "への口",
    textures: new Set([CostumeTextureID.MouthStronglyClothed]),
  },
  [CostumeID.MouthWaved]: {
    category: "口",
    name: "波線っぽい口",
    textures: new Set([CostumeTextureID.MouthWaved]),
  },
  [CostumeID.MouthDot]: {
    category: "口",
    name: "点",
    textures: new Set([CostumeTextureID.MouthDot]),
  },
  [CostumeID.MouthVampire]: {
    category: "口",
    name: "牙笑顔（ヴァンパイア）",
    textures: new Set([CostumeTextureID.MouthVampire]),
  },
  // Arms
  [CostumeID.ArmsNone]: {
    category: "腕",
    name: "なし",
    textures: (costumes: CostumeSet) => {
      return new Set([]);
    },
  },
  [CostumeID.ArmsBye]: {
    category: "腕",
    name: "ばいばい",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "服装");
      if (clothes.has(CostumeID.ClothesNormal)) {
        return new Set([CostumeTextureID.ArmsByeNormal]);
      } else if (costumes.has(CostumeID.ClothesSummer)) {
        return new Set([CostumeTextureID.ArmsByeSummer]);
      } else if (costumes.has(CostumeID.ClothesSchool)) {
        return new Set([CostumeTextureID.ArmsByeSchool]);
      } else if (costumes.has(CostumeID.ClothesFurisode)) {
        return new Set([CostumeTextureID.ArmsByeFurisode]);
      } else if (costumes.has(CostumeID.ClothesFurisodeWithShawl)) {
        return new Set([CostumeTextureID.ArmsByeFurisodeWithShawl]);
      } else if (costumes.has(CostumeID.ClothesMaid)) {
        return new Set([CostumeTextureID.ArmsByeMaid]);
      } else if (costumes.has(CostumeID.ClothesWhiteTurtleneck)) {
        return new Set([CostumeTextureID.ArmsByeWhiteTurtleneck]);
      } else if (costumes.has(CostumeID.ClothesBlackShirt)) {
        return new Set([CostumeTextureID.ArmsByeBlackShirt]);
      } else if (costumes.has(CostumeID.ClothesBlackShirtWithSacoche)) {
        return new Set([CostumeTextureID.ArmsByeBlackShirtWithSacoche]);
      } else if (costumes.has(CostumeID.ClothesAku)) {
        return new Set([CostumeTextureID.ArmsByeAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.ArmsPen]: {
    category: "腕",
    name: "鉛筆持ち",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "服装");
      if (clothes.has(CostumeID.ClothesNormal)) {
        return new Set([CostumeTextureID.ArmsPenNormal]);
      } else if (costumes.has(CostumeID.ClothesSummer)) {
        return new Set([CostumeTextureID.ArmsPenSummer]);
      } else if (costumes.has(CostumeID.ClothesSchool)) {
        return new Set([CostumeTextureID.ArmsPenSchool]);
      } else if (costumes.has(CostumeID.ClothesFurisode)) {
        return new Set([CostumeTextureID.ArmsPenFurisode]);
      } else if (costumes.has(CostumeID.ClothesFurisodeWithShawl)) {
        return new Set([CostumeTextureID.ArmsPenFurisodeWithShawl]);
      } else if (costumes.has(CostumeID.ClothesMaid)) {
        return new Set([CostumeTextureID.ArmsPenMaid]);
      } else if (costumes.has(CostumeID.ClothesWhiteTurtleneck)) {
        return new Set([CostumeTextureID.ArmsPenWhiteTurtleneck]);
      } else if (costumes.has(CostumeID.ClothesBlackShirt)) {
        return new Set([CostumeTextureID.ArmsPenBlackShirt]);
      } else if (costumes.has(CostumeID.ClothesBlackShirtWithSacoche)) {
        return new Set([CostumeTextureID.ArmsPenBlackShirtWithSacoche]);
      } else if (costumes.has(CostumeID.ClothesAku)) {
        return new Set([CostumeTextureID.ArmsPenAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.ArmsCat]: {
    category: "腕",
    name: "猫の手",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "服装");
      if (clothes.has(CostumeID.ClothesNormal)) {
        return new Set([CostumeTextureID.ArmsCatNormal]);
      } else if (costumes.has(CostumeID.ClothesSummer)) {
        return new Set([CostumeTextureID.ArmsCatSummer]);
      } else if (costumes.has(CostumeID.ClothesSchool)) {
        return new Set([CostumeTextureID.ArmsCatSchool]);
      } else if (costumes.has(CostumeID.ClothesFurisode)) {
        return new Set([CostumeTextureID.ArmsCatFurisode]);
      } else if (costumes.has(CostumeID.ClothesFurisodeWithShawl)) {
        return new Set([CostumeTextureID.ArmsCatFurisodeWithShawl]);
      } else if (costumes.has(CostumeID.ClothesMaid)) {
        return new Set([CostumeTextureID.ArmsCatMaid]);
      } else if (costumes.has(CostumeID.ClothesWhiteTurtleneck)) {
        return new Set([CostumeTextureID.ArmsCatWhiteTurtleneck]);
      } else if (costumes.has(CostumeID.ClothesBlackShirt)) {
        return new Set([CostumeTextureID.ArmsCatBlackShirt]);
      } else if (costumes.has(CostumeID.ClothesBlackShirtWithSacoche)) {
        return new Set([CostumeTextureID.ArmsCatBlackShirtWithSacoche]);
      } else if (costumes.has(CostumeID.ClothesAku)) {
        return new Set([CostumeTextureID.ArmsCatAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.ArmsGao]: {
    category: "腕",
    name: "がおー",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "服装");
      if (clothes.has(CostumeID.ClothesNormal)) {
        return new Set([CostumeTextureID.ArmsGaoNormal]);
      } else if (costumes.has(CostumeID.ClothesSummer)) {
        return new Set([CostumeTextureID.ArmsGaoSummer]);
      } else if (costumes.has(CostumeID.ClothesSchool)) {
        return new Set([CostumeTextureID.ArmsGaoSchool]);
      } else if (costumes.has(CostumeID.ClothesFurisode)) {
        return new Set([CostumeTextureID.ArmsGaoFurisode]);
      } else if (costumes.has(CostumeID.ClothesFurisodeWithShawl)) {
        return new Set([CostumeTextureID.ArmsGaoFurisodeWithShawl]);
      } else if (costumes.has(CostumeID.ClothesMaid)) {
        return new Set([CostumeTextureID.ArmsGaoMaid]);
      } else if (costumes.has(CostumeID.ClothesWhiteTurtleneck)) {
        return new Set([CostumeTextureID.ArmsGaoWhiteTurtleneck]);
      } else if (costumes.has(CostumeID.ClothesBlackShirt)) {
        return new Set([CostumeTextureID.ArmsGaoBlackShirt]);
      } else if (costumes.has(CostumeID.ClothesBlackShirtWithSacoche)) {
        return new Set([CostumeTextureID.ArmsGaoBlackShirtWithSacoche]);
      } else if (costumes.has(CostumeID.ClothesAku)) {
        return new Set([CostumeTextureID.ArmsGaoAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.ArmsIndexFinger]: {
    category: "腕",
    name: "人差し指",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "服装");
      if (clothes.has(CostumeID.ClothesNormal)) {
        return new Set([CostumeTextureID.ArmsIndexFingerNormal]);
      } else if (costumes.has(CostumeID.ClothesSummer)) {
        return new Set([CostumeTextureID.ArmsIndexFingerSummer]);
      } else if (costumes.has(CostumeID.ClothesSchool)) {
        return new Set([CostumeTextureID.ArmsIndexFingerSchool]);
      } else if (costumes.has(CostumeID.ClothesFurisode)) {
        return new Set([CostumeTextureID.ArmsIndexFingerFurisode]);
      } else if (costumes.has(CostumeID.ClothesFurisodeWithShawl)) {
        return new Set([CostumeTextureID.ArmsIndexFingerFurisodeWithShawl]);
      } else if (costumes.has(CostumeID.ClothesMaid)) {
        return new Set([CostumeTextureID.ArmsIndexFingerMaid]);
      } else if (costumes.has(CostumeID.ClothesWhiteTurtleneck)) {
        return new Set([CostumeTextureID.ArmsIndexFingerWhiteTurtleneck]);
      } else if (costumes.has(CostumeID.ClothesBlackShirt)) {
        return new Set([CostumeTextureID.ArmsIndexFingerBlackShirt]);
      } else if (costumes.has(CostumeID.ClothesBlackShirtWithSacoche)) {
        return new Set([CostumeTextureID.ArmsIndexFingerBlackShirtWithSacoche]);
      } else if (costumes.has(CostumeID.ClothesAku)) {
        return new Set([CostumeTextureID.ArmsIndexFingerAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.ArmsToMouth]: {
    category: "腕",
    name: "口元に手",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "服装");
      if (clothes.has(CostumeID.ClothesNormal)) {
        return new Set([CostumeTextureID.ArmsToMouthNormal]);
      } else if (costumes.has(CostumeID.ClothesSummer)) {
        return new Set([CostumeTextureID.ArmsToMouthSummer]);
      } else if (costumes.has(CostumeID.ClothesSchool)) {
        return new Set([CostumeTextureID.ArmsToMouthSchool]);
      } else if (costumes.has(CostumeID.ClothesFurisode)) {
        return new Set([CostumeTextureID.ArmsToMouthFurisode]);
      } else if (costumes.has(CostumeID.ClothesFurisodeWithShawl)) {
        return new Set([CostumeTextureID.ArmsToMouthFurisodeWithShawl]);
      } else if (costumes.has(CostumeID.ClothesMaid)) {
        return new Set([CostumeTextureID.ArmsToMouthMaid]);
      } else if (costumes.has(CostumeID.ClothesWhiteTurtleneck)) {
        return new Set([CostumeTextureID.ArmsToMouthWhiteTurtleneck]);
      } else if (costumes.has(CostumeID.ClothesBlackShirt)) {
        return new Set([CostumeTextureID.ArmsToMouthBlackShirt]);
      } else if (costumes.has(CostumeID.ClothesBlackShirtWithSacoche)) {
        return new Set([CostumeTextureID.ArmsToMouthBlackShirtWithSacoche]);
      } else if (costumes.has(CostumeID.ClothesAku)) {
        return new Set([CostumeTextureID.ArmsToMouthAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.ArmsHeart]: {
    category: "腕",
    name: "手をハートにして",
    textures: (costumes: CostumeSet) => {
      return new Set([CostumeTextureID.ArmsHeart]);
    },
  },
  [CostumeID.ArmsAkinium]: {
    category: "腕",
    name: "アキニウム光線",
    textures: (costumes: CostumeSet) => {
      return new Set([CostumeTextureID.ArmsAkinium]);
    },
  },
  // Clothes
  [CostumeID.ClothesNormal]: {
    category: "服装",
    name: "通常",
    textures: new Set([CostumeTextureID.ClothesNormal]),
  },
  [CostumeID.ClothesSummer]: {
    category: "服装",
    name: "夏服",
    textures: new Set([CostumeTextureID.ClothesSummer]),
  },
  [CostumeID.ClothesSchool]: {
    category: "服装",
    name: "学生服",
    textures: new Set([CostumeTextureID.ClothesSchool]),
  },
  [CostumeID.ClothesFurisode]: {
    category: "服装",
    name: "振り袖",
    textures: new Set([CostumeTextureID.ClothesFurisode]),
  },
  [CostumeID.ClothesFurisodeWithShawl]: {
    category: "服装",
    name: "振り袖 + ショール",
    textures: new Set([CostumeTextureID.ClothesFurisodeWithShawl]),
  },
  [CostumeID.ClothesMaid]: {
    category: "服装",
    name: "メイド",
    textures: new Set([CostumeTextureID.ClothesMaid]),
  },
  [CostumeID.ClothesWhiteTurtleneck]: {
    category: "服装",
    name: "白タートルネック",
    textures: new Set([CostumeTextureID.ClothesWhiteTurtleneck]),
  },
  [CostumeID.ClothesBlackShirt]: {
    category: "服装",
    name: "黒シャツ",
    textures: new Set([CostumeTextureID.ClothesBlackShirt]),
  },
  [CostumeID.ClothesBlackShirtWithSacoche]: {
    category: "服装",
    name: "黒シャツ + サコッシュ",
    textures: new Set([CostumeTextureID.ClothesBlackShirtWithSacoche]),
  },
  [CostumeID.ClothesAku]: {
    category: "服装",
    name: "アクくんカラー",
    textures: new Set([CostumeTextureID.ClothesAku]),
  },
  // Caps
  [CostumeID.CapNone]: {
    category: "帽子",
    name: "なし",
    textures: new Set([]),
  },
  [CostumeID.CapCrown]: {
    category: "帽子",
    name: "王冠",
    textures: new Set([CostumeTextureID.AccessoryCrown]), // もともとアクセサリだったが帽子に変更
  },
  [CostumeID.CapWhite]: {
    category: "帽子",
    name: "おもち",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "髪型");
      if (clothes.has(CostumeID.HairNormal)) {
        return new Set([
          CostumeTextureID.CapWhiteNormal,
          CostumeTextureID.CapWhiteLining,
        ]);
      } else if (costumes.has(CostumeID.HairTied)) {
        return new Set([
          CostumeTextureID.CapWhiteTied,
          CostumeTextureID.CapWhiteLining,
        ]);
      } else if (costumes.has(CostumeID.HairFurisode)) {
        return new Set([
          CostumeTextureID.CapWhiteFurisode,
          CostumeTextureID.CapWhiteLining,
        ]);
      } else if (costumes.has(CostumeID.HairBraidMaid)) {
        return new Set([
          CostumeTextureID.CapWhiteBraidMaid,
          CostumeTextureID.CapWhiteLining,
        ]);
      } else if (costumes.has(CostumeID.HairBraidShort)) {
        return new Set([
          CostumeTextureID.CapWhiteBraidShort,
          CostumeTextureID.CapWhiteLining,
        ]);
      } else if (costumes.has(CostumeID.HairBrushedUp)) {
        return new Set([
          CostumeTextureID.CapWhiteBrushedUp,
          CostumeTextureID.CapWhiteLining,
        ]);
      } else if (costumes.has(CostumeID.HairAku)) {
        return new Set([
          CostumeTextureID.CapWhiteAku,
          CostumeTextureID.CapWhiteLining,
        ]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.CapSchool]: {
    category: "帽子",
    name: "学生帽",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "髪型");
      if (clothes.has(CostumeID.HairNormal)) {
        return new Set([CostumeTextureID.CapSchoolNormal]);
      } else if (costumes.has(CostumeID.HairTied)) {
        return new Set([CostumeTextureID.CapSchoolTied]);
      } else if (costumes.has(CostumeID.HairFurisode)) {
        return new Set([CostumeTextureID.CapSchoolFurisode]);
      } else if (costumes.has(CostumeID.HairBraidMaid)) {
        return new Set([CostumeTextureID.CapSchoolBraidMaid]);
      } else if (costumes.has(CostumeID.HairBraidShort)) {
        return new Set([CostumeTextureID.CapSchoolBraidShort]);
      } else if (costumes.has(CostumeID.HairBrushedUp)) {
        return new Set([CostumeTextureID.CapSchoolBrushedUp]);
      } else if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.CapSchoolAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.CapCasquette]: {
    category: "帽子",
    name: "キャスケット",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "髪型");
      if (clothes.has(CostumeID.HairNormal)) {
        return new Set([CostumeTextureID.CapCasquetteNormal]);
      } else if (costumes.has(CostumeID.HairTied)) {
        return new Set([CostumeTextureID.CapCasquetteTied]);
      } else if (costumes.has(CostumeID.HairFurisode)) {
        return new Set([CostumeTextureID.CapCasquetteFurisode]);
      } else if (costumes.has(CostumeID.HairBraidMaid)) {
        return new Set([CostumeTextureID.CapCasquetteBraidMaid]);
      } else if (costumes.has(CostumeID.HairBraidShort)) {
        return new Set([CostumeTextureID.CapCasquetteBraidShort]);
      } else if (costumes.has(CostumeID.HairBrushedUp)) {
        return new Set([CostumeTextureID.CapCasquetteBrushedUp]);
      } else if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.CapCasquetteAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.CapHeaddress]: {
    category: "帽子",
    name: "メイドカチューシャ",
    textures: (costumes: CostumeSet) => {
      const clothes = filterCostumesByCategory(costumes, "髪型");
      if (clothes.has(CostumeID.HairNormal)) {
        return new Set([CostumeTextureID.CapHeaddressNormal]);
      } else if (costumes.has(CostumeID.HairTied)) {
        return new Set([CostumeTextureID.CapHeaddressTied]);
      } else if (costumes.has(CostumeID.HairFurisode)) {
        return new Set([CostumeTextureID.CapHeaddressFurisode]);
      } else if (costumes.has(CostumeID.HairBraidMaid)) {
        return new Set([CostumeTextureID.CapHeaddressBraidMaid]);
      } else if (costumes.has(CostumeID.HairBraidShort)) {
        return new Set([CostumeTextureID.CapHeaddressBraidShort]);
      } else if (costumes.has(CostumeID.HairBrushedUp)) {
        return new Set([CostumeTextureID.CapHeaddressBrushedUp]);
      } else if (costumes.has(CostumeID.HairAku)) {
        return new Set([CostumeTextureID.CapHeaddressAku]);
      }
      throw new Error(`The given costume set doen not contains clothes.`);
    },
  },
  [CostumeID.CapHalloween]: {
    category: "帽子",
    name: "黒猫耳",
    textures: (costumes: CostumeSet) => {
      return new Set([CostumeTextureID.CapHalloween]);
    },
  },
  // Glasses
  [CostumeID.GlassesNone]: {
    category: "メガネ",
    name: "なし",
    textures: new Set([]),
  },
  [CostumeID.GlassesNormal]: {
    category: "メガネ",
    name: "メガネ",
    textures: new Set([CostumeTextureID.Glasses]),
  },
  [CostumeID.GlassesCinnamoroll]: {
    category: "メガネ",
    name: "流行りの眼鏡",
    textures: new Set([CostumeTextureID.GlassesCinnamoroll]),
  },
  // Earrings
  [CostumeID.EarringsNone]: {
    category: "耳飾り",
    name: "なし",
    textures: new Set([]),
  },
  [CostumeID.EarringsVampire]: {
    category: "耳飾り",
    name: "カボチャ耳飾り（ヴァンパイア）",
    textures: new Set([CostumeTextureID.EarringsVampire]),
  },
  [CostumeID.EarringsFur]: {
    category: "耳飾り",
    name: "振り袖用イヤリング",
    textures: new Set([CostumeTextureID.EarringsFur]),
  },
  // Accessories
  [CostumeID.AccessoryAngryMark]: {
    category: "小物",
    name: "イカリマーク",
    textures: new Set([CostumeTextureID.AccessoryAngryMark]),
  },
  [CostumeID.AccessoryTeardrop]: {
    category: "小物",
    name: "涙",
    textures: new Set([CostumeTextureID.AccessoryTeardrop]),
  },
  [CostumeID.AccessoryMicrophone]: {
    category: "小物",
    name: "インカム",
    textures: new Set([CostumeTextureID.AccessoryMicrophone]),
  },
  [CostumeID.AccessoryHairpinsVampire]: {
    category: "小物",
    name: "黒ヘアピン（ヴァンパイア）",
    textures: new Set([CostumeTextureID.AccessoryHairpinsVampire]),
  },
  [CostumeID.AccessoryHairpin]: {
    category: "小物",
    name: "ねこヘアピン",
    textures: new Set([CostumeTextureID.AccessoryHairpin]),
  },
  [CostumeID.AccessorySparkle]: {
    category: "小物",
    name: "キラキラ",
    textures: new Set([CostumeTextureID.AccessorySparkle]),
  },
  [CostumeID.AccessoryHearts]: {
    category: "小物",
    name: "漂うハート",
    textures: new Set([CostumeTextureID.AccessoryHearts]),
  },
  [CostumeID.AccessoryHead]: {
    category: "小物",
    name: "振袖用髪飾り",
    textures: new Set([CostumeTextureID.AccessoryHead]),
  },
  [CostumeID.AccessoryWing]: {
    category: "小物",
    name: "天使の羽",
    textures: new Set([CostumeTextureID.AccessoryWing]),
  },
  [CostumeID.AccessoryCheek1]: {
    category: "小物",
    name: "頬の赤み1",
    textures: new Set([CostumeTextureID.AccessoryCheek1]),
  },
  [CostumeID.AccessoryCheek2]: {
    category: "小物",
    name: "頬の赤み2",
    textures: new Set([CostumeTextureID.AccessoryCheek2]),
  },
  [CostumeID.AccessoryCheek3]: {
    category: "小物",
    name: "頬の赤み3",
    textures: new Set([CostumeTextureID.AccessoryCheek3]),
  },
  // Backgrounds
  [CostumeID.BackgroundCaffe]: {
    category: "背景",
    name: "カフェ",
    textures: new Set([CostumeTextureID.BackgroundCaffe]),
  },
  [CostumeID.BackgroundLivingRoom]: {
    category: "背景",
    name: "リビング",
    textures: new Set([CostumeTextureID.BackgroundLivingRoom]),
  },
  [CostumeID.BackgroundJapaneseRoom]: {
    category: "背景",
    name: "和室",
    textures: new Set([CostumeTextureID.BackgroundJapaneseRoom]),
  },
  [CostumeID.BackgroundAquarium]: {
    category: "背景",
    name: "水族館",
    textures: new Set([CostumeTextureID.BackgroundAquarium]),
  },
  [CostumeID.BackgroundAmusementPark1]: {
    category: "背景",
    name: "遊園地1",
    textures: new Set([CostumeTextureID.BackgroundAmusementPark1]),
  },
  [CostumeID.BackgroundAmusementPark2]: {
    category: "背景",
    name: "遊園地2",
    textures: new Set([CostumeTextureID.BackgroundAmusementPark2]),
  },
  [CostumeID.BackgroundFlowerGarden]: {
    category: "背景",
    name: "コスモス畑",
    textures: new Set([CostumeTextureID.BackgroundFlowerGarden]),
  },
  [CostumeID.BackgroundClassRoom]: {
    category: "背景",
    name: "教室",
    textures: new Set([CostumeTextureID.BackgroundClassRoom]),
  },
  [CostumeID.BackgroundNightView]: {
    category: "背景",
    name: "夜景",
    textures: new Set([CostumeTextureID.BackgroundNightView]),
  },
  [CostumeID.BackgroundNightSky]: {
    category: "背景",
    name: "夜空",
    textures: new Set([CostumeTextureID.BackgroundNightSky]),
  },
  [CostumeID.BackgroundSpring]: {
    category: "背景",
    name: "春",
    textures: new Set([CostumeTextureID.BackgroundSpring]),
  },
  [CostumeID.BackgroundSummer1]: {
    category: "背景",
    name: "夏1",
    textures: new Set([CostumeTextureID.BackgroundSummer1]),
  },
  [CostumeID.BackgroundSummer2]: {
    category: "背景",
    name: "夏2",
    textures: new Set([CostumeTextureID.BackgroundSummer2]),
  },
  [CostumeID.BackgroundSummer3]: {
    category: "背景",
    name: "夏3",
    textures: new Set([CostumeTextureID.BackgroundSummer3]),
  },
  [CostumeID.BackgroundFall1]: {
    category: "背景",
    name: "秋1",
    textures: new Set([CostumeTextureID.BackgroundFall1]),
  },
  [CostumeID.BackgroundFall2]: {
    category: "背景",
    name: "秋2",
    textures: new Set([CostumeTextureID.BackgroundFall2]),
  },
  [CostumeID.BackgroundWinter1]: {
    category: "背景",
    name: "秋3",
    textures: new Set([CostumeTextureID.BackgroundWinter1]),
  },
  [CostumeID.BackgroundWinter2]: {
    category: "背景",
    name: "冬",
    textures: new Set([CostumeTextureID.BackgroundWinter2]),
  },
  [CostumeID.BackgroundSpace]: {
    category: "背景",
    name: "宇宙",
    textures: new Set([CostumeTextureID.BackgroundSpace]),
  },
  [CostumeID.BackgroundTransparent]: {
    category: "背景",
    name: "透過",
    textures: new Set([]),
  },
};

const defaultCostumeSet: CostumeSet = new Set([
  CostumeID.HairNormal,
  CostumeID.EyebrowsNormal,
  CostumeID.EyesNormal,
  CostumeID.EyeColorNormal,
  CostumeID.MouthClosed,
  CostumeID.ArmsNone,
  CostumeID.ClothesNormal,
  CostumeID.CapNone,
  CostumeID.GlassesNone,
  CostumeID.EarringsNone,
  CostumeID.BackgroundCaffe,
]);

const filterCostumesByCategory = (
  costumes: CostumeSet,
  category: CostumeCategory
) => {
  return new Set(
    Array.from(costumes.values()).filter(
      (costume) => costumeRegistry[costume].category === category
    )
  );
};

export {
  CostumeID,
  costumeRegistry,
  costumeCategoryRegistry,
  defaultCostumeSet,
  filterCostumesByCategory,
};

export type {
  CostumeSet,
  CostumeRegistry,
  CostumeCategory,
  CostumeCategoryRegistry,
};
